import React, { useMemo, useState, useEffect } from "react";
import { useQuery, gql } from "@apollo/client";
import { 
  FileText, 
  Gavel, 
  ScanLine, 
  ArrowRight,
  Activity,
  Search,
  Check,
  Eye,
  Siren,
  ClipboardList,
  ChevronsUpDown,
  Star,
  AlertTriangle,
  RefreshCw,
  Plus,
  Pill,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { isoDateToMDY } from "@/utils/common";
import { Badge } from "@/components/ui/badge";
import { TabHeader } from "./shared/components/TabHeader";
import { TabSpinner } from "./shared/components/TabSpinner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FormView } from "../participantDocuments/lib/formView";
import { ScanPreview } from "./scans/modals/PreviewScan";
import { useUserPermissions } from "@/store/useUserPermissions";
import { hasAnyPolicy } from "@/utils/policies";

// --- QUERIES ---

const GET_SUMMARY_DATA = gql`
  query GetSummaryData($odrnumber: Int!, $odrString: String!, $includeCoordinatedCare: Boolean!, $includeProgressReports: Boolean!) {
    getParticipantByODRNumber(odrnumber: $odrnumber) {
      id
      programDetails {
        programDescription
        entryDate
        exitDate
        active
      }
      bookingDetails {
        bookingNumber
        arrestDate
        releaseDate
        cases {
          caseNumber
          charges {
            charge
          }
          hearings {
            hearingDate
            details
            type
          }
        }
      }
      scans {
        id
        filedate
        filelabel
        filetype
      }
      notes { # NEW: General notes
        note
        date
        author
        program
      }
      keyEvents { # NEW: Key Events
        uniqueid
        startdate
        enddate
        event
        notes
        entryby
        entrydate
      }
      documents {
        id
        type
        date
        tags
      }
      incidents {
        id
        incidentDate
        incidentTime
        reportCategory
        incidentLocationName
        program
        loginName
        primaryContactName
        incidentClientsNames
        incidentClientsCid
      }
    }
    coordinatedCareNotes(odrnumber: $odrString) @include(if: $includeCoordinatedCare) {
      id
      noteEntryDate
      procedurenote
      serviceType
      entryby
    }
    progressReportsForParticipant(identifier: $odrnumber, page: 1, pageSize: 500, sortField: "date", sortDirection: "DESC") @include(if: $includeProgressReports) {
      rows { id cells { key value } }
    }
  }
`;

const GET_PARTICIPANT_DOCUMENT = gql`
  query GetParticipantDocument($documentID: Int!) {
    getParticipantDocument(documentID: $documentID) {
      id
      contents
      template
      tags
      type
    }
  }
`;

// --- TYPES ---

type EventType = 
  | 'enrollment' 
  | 'arrest' 
  | 'hearing' 
  | 'note'           // General notes
  | 'coordinated_care' 
  | 'scan'
  | 'document'
  | 'incident'
  | 'key_event'
  | 'progress_report';

interface TimelineEvent {
  id: string;
  date: Date;
  endDate?: Date;
  type: EventType;
  title: string;
  description?: string;
  metadata?: any;
  isRange?: boolean;
}

const eventTypeLabels: Record<EventType, string> = {
  enrollment: "Enrollments",
  arrest: "Legal",
  hearing: "Hearings",
  note: "General Note",
  coordinated_care: "Care",
  scan: "Scans",
  document: "Documents",
  incident: "Incidents",
  key_event: "Key Events",
  progress_report: "Progress Reports"
};

type DocumentViewState = {
  template: any;
  contents: any;
  type?: string;
  tags?: string[];
};

const parseJsonSafe = (value?: string | null) => {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch (error) {
    console.error("Failed to parse document payload", error);
    return null;
  }
};

// --- COMPONENT ---

type SummaryProps = {
  odrnumber: number;
  onAddEntry?: () => void;
  onOpenMedcall?: () => void;
  onOpenProgressReports?: () => void;
};

export const Summary = ({ odrnumber, onAddEntry, onOpenMedcall, onOpenProgressReports }: SummaryProps) => {
  const { policies } = useUserPermissions();
  const canReadCoordinatedCareNotes = hasAnyPolicy(policies, [
    'program:participant:coordinatedcare~full'
  ]);
  const canReadProgressReports = hasAnyPolicy(policies, ['tools:progressreportaccess~full']);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandAll, setExpandAll] = useState(false); // NEW: Global expand state
  
  // Initialize filters from localStorage or default to selected set
  const buildDefaultFilters = () => new Set([
    'enrollment',
    'arrest',
    'hearing',
    'note',
    'incident',
    'key_event',
    'progress_report'
  ] as EventType[]);
  const [selectedFilters, setSelectedFilters] = useState<Set<EventType>>(() => {
    const defaults = buildDefaultFilters();
    try {
      const saved = localStorage.getItem('summaryFilters');
      if (!saved) return defaults;
      const parsed = JSON.parse(saved);
      if (!Array.isArray(parsed)) return defaults;
      parsed.forEach((type: EventType) => defaults.add(type));
      return defaults;
    } catch (e) {
      console.error("Failed to load filters", e);
      return defaults;
    }
  });

  const [selectedDocumentId, setSelectedDocumentId] = useState<number | null>(null);
  const [documentModalOpen, setDocumentModalOpen] = useState(false);
  const [documentView, setDocumentView] = useState<DocumentViewState | null>(null);
  const [selectedScanId, setSelectedScanId] = useState<number | null>(null);

  const { data, loading, error } = useQuery(GET_SUMMARY_DATA, {
    variables: { 
      odrnumber: odrnumber,
      odrString: odrnumber.toString(),
      includeCoordinatedCare: canReadCoordinatedCareNotes,
      includeProgressReports: canReadProgressReports
    }
  });

  const toggleFilter = (type: EventType) => {
    const newFilters = new Set(selectedFilters);
    if (newFilters.has(type)) {
      newFilters.delete(type);
    } else {
      newFilters.add(type);
    }
    setSelectedFilters(newFilters);
    
    try {
      localStorage.setItem('summaryFilters', JSON.stringify(Array.from(newFilters)));
    } catch (e) {
      console.error("Failed to save filters", e);
    }
  };

  const handleViewDocument = (documentId?: number) => {
    if (!documentId) return;
    setDocumentView(null);
    setSelectedDocumentId(documentId);
    setDocumentModalOpen(true);
  };

  const closeDocumentModal = () => {
    setDocumentModalOpen(false);
    setSelectedDocumentId(null);
    setDocumentView(null);
  };

  const toScanId = (value: any) => {
    if (typeof value === 'number' && !Number.isNaN(value)) return value;
    if (typeof value === 'string') {
      const parsed = parseInt(value, 10);
      if (!Number.isNaN(parsed)) return parsed;
    }
    return null;
  };

  const handleSelectScan = (scanId: any) => {
    const parsed = toScanId(scanId);
    if (parsed == null) return;
    setSelectedScanId(parsed);
  };

  const { loading: documentLoading, error: documentError } = useQuery(
    GET_PARTICIPANT_DOCUMENT,
    {
      variables: { documentID: selectedDocumentId ?? 0 },
      skip: !selectedDocumentId,
      onCompleted: (result) => {
        if (!result?.getParticipantDocument) return;
        const parsedTemplate = parseJsonSafe(result.getParticipantDocument.template);
        const parsedContents = parseJsonSafe(result.getParticipantDocument.contents);
        if (!parsedTemplate || !parsedContents) return;
        setDocumentView({
          template: parsedTemplate,
          contents: parsedContents,
          type: result.getParticipantDocument.type,
          tags: result.getParticipantDocument.tags
        });
      }
    }
  );

  const events = useMemo(() => {
    if (!data) return [];
    const participant = data.getParticipantByODRNumber;
    const generalNotes = participant.notes || []; // NEW: General notes source
    const ccNotes = data.coordinatedCareNotes || [];
    const keyEvents = participant.keyEvents || []; // NEW: Key Events source
    const documents = participant.documents || [];
    const incidents = participant.incidents || [];
    const progressReports = data.progressReportsForParticipant?.rows || [];
    
    let timeline: TimelineEvent[] = [];

    // 1. Enrollments
    participant?.programDetails?.forEach((prog: any, idx: number) => {
      if (prog.entryDate) {
        timeline.push({
          id: `prog-${idx}`,
          date: new Date(prog.entryDate),
          endDate: prog.exitDate ? new Date(prog.exitDate) : undefined,
          type: 'enrollment',
          title: `Enrollment: ${prog.programDescription?.toUpperCase()}`,
          description: prog.active ? "Active" : "Completed",
          isRange: true
        });
      }
    });

    // 2. Arrests & Bookings
    participant?.bookingDetails?.forEach((booking: any, bookingIndex: number) => {
      if (booking.arrestDate) {
        timeline.push({
          id: `booking-${booking.bookingNumber}-${bookingIndex}`,
          date: new Date(booking.arrestDate),
          endDate: booking.releaseDate ? new Date(booking.releaseDate) : undefined,
          type: 'arrest',
          title: `Booking`,
          description: `#${booking.bookingNumber}`,
          isRange: true
        });
      }

      // Hearings
      booking.cases?.forEach((c: any, caseIndex: number) => {
        c.hearings?.forEach((h: any, hIdx: number) => {
          if (h.hearingDate) {
            timeline.push({
              id: `hearing-${booking.bookingNumber}-${bookingIndex}-${caseIndex}-${hIdx}`,
              date: new Date(h.hearingDate),
              type: 'hearing',
              title: `Hearing`,
              description: `${h.type} - ${h.details || 'No details'}`, 
            });
          }
        });
      });
    });

    // 3. Scans
    const scansByDate: Record<string, any[]> = {};
    participant?.scans?.forEach((scan: any) => {
      if (scan.filedate) {
          const dateKey = scan.filedate.split('T')[0]; 
          if (!scansByDate[dateKey]) {
              scansByDate[dateKey] = [];
          }
          scansByDate[dateKey].push(scan);
      }
    });

    Object.entries(scansByDate).forEach(([dateKey, dayScans]) => {
        if (dayScans.length === 1) {
            const scan = dayScans[0];
            timeline.push({
              id: `scan-${scan.id}`,
              date: new Date(scan.filedate),
              type: 'scan',
              title: 'Scan',
              description: scan.filelabel || scan.filetype,
              metadata: { scanId: scan.id } 
            });
        } else {
            timeline.push({
                id: `scans-${dateKey}`,
                date: new Date(dayScans[0].filedate),
                type: 'scan',
                title: `Scans (${dayScans.length})`,
                description: dayScans.map((s:any) => s.filelabel || s.filetype).join(", "), 
                metadata: { 
                    scans: dayScans.map((s:any) => ({
                        id: s.id,
                        label: s.filelabel || s.filetype
                    })) 
                }
            });
        }
    });

    // 4. General Notes (from participant.notes)
    generalNotes.forEach((note: any, idx: number) => { // NEW processing
      if (note.date) {
        timeline.push({
          id: `gennote-${idx}`,
          date: new Date(note.date),
          type: 'note', // This is now 'note'
          title: `General Note`,
          description: note.note,
          metadata: { author: note.author, program: note.program }
        });
      }
    });

    // 5. Coordinated Care Notes
  ccNotes.forEach((note: any) => {
    if (note.noteEntryDate) {
      timeline.push({
        id: `cc-${note.id}`,
        date: new Date(note.noteEntryDate),
          type: 'coordinated_care',
          title: 'Care',
          description: note.procedurenote,
          metadata: { type: note.serviceType, author: note.entryby }
        });
      }
    });

    // 7. Incidents
    incidents.forEach((incident: any) => {
      if (!incident.incidentDate) return;
      const reporter = incident.loginName || incident.primaryContactName;
      const descriptionParts = [
        incident.reportCategory,
        incident.incidentLocationName
      ].filter(Boolean);

      timeline.push({
        id: `incident-${incident.id}`,
        date: new Date(incident.incidentDate),
        type: 'incident',
        title: 'Incident',
        description: descriptionParts.join(" - ") || 'Incident reported',
        metadata: { 
          author: reporter,
          program: incident.program,
          clients: incident.incidentClientsNames,
          cids: incident.incidentClientsCid
        }
      });
    });

    // 7. Key Events
    keyEvents.forEach((ke: any) => {
      if (ke.startdate || ke.entrydate) {
        timeline.push({
          id: `keyevent-${ke.uniqueid}`,
          date: new Date(ke.startdate || ke.entrydate),
          endDate: ke.enddate ? new Date(ke.enddate) : undefined,
          type: 'key_event',
          title: ke.event || 'Key Event',
          description: ke.notes,
          metadata: { author: ke.entryby },
          isRange: !!ke.enddate
        });
      }
    });

    documents.forEach((doc: any) => {
      if (!doc?.date) return;
      const tags = Array.isArray(doc.tags) ? doc.tags : [];
      timeline.push({
        id: `document-${doc.id}`,
        date: new Date(doc.date),
        type: 'document',
        title: doc.type ? `Document: ${doc.type}` : 'Document',
        description: doc.id ? `Doc ID: ${doc.id}` : "Participant document",
        metadata: { 
          documentId: doc.id,
          documentType: doc.type,
          tags,
          documentsUrl: `/documents/${odrnumber}`
        }
      });
    });

    progressReports.forEach((report: any) => {
      const cells = Array.isArray(report?.cells) ? report.cells : [];
      const value = (field: string) => String(cells.find((cell: any) => cell?.key === field)?.value || '').trim();
      const reportDate = value('date') || value('court_date');
      if (!reportDate) return;
      const description = [value('appearance').replace(/_/g, ' '), value('case_number'), value('icms_provider_name')].filter(Boolean).join(' · ');
      timeline.push({
        id: `progress-report-${report.id}`,
        date: new Date(reportDate),
        type: 'progress_report',
        title: 'Progress Report',
        description: description || 'Progress report filed',
        metadata: { reportId: report.id }
      });
    });

    return timeline.sort((a, b) => b.date.getTime() - a.date.getTime());
  }, [data, odrnumber]);
  const filteredEvents = useMemo(() => {
    return events.filter(e => {
      const matchesSearch = searchTerm === "" || 
        e.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        e.description?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesType = selectedFilters.has(e.type);
      return matchesSearch && matchesType;
    });
  }, [events, searchTerm, selectedFilters]);

  const renderDocumentModalContent = () => {
    if (documentError) return <div className="text-sm text-[var(--semantic-error)]">Unable to load document.</div>;
    if (documentLoading && !documentView) {
      return (
        <div className="flex h-40 items-center justify-center text-sm text-[var(--neutral-gray-80)]">
          Loading document...
        </div>
      );
    }
    if (!documentView) return <div className="text-sm text-[var(--neutral-gray-80)]">Document content is not available.</div>;
    return (
      <FormView formTemplate={documentView.template} formState={documentView.contents} />
    );
  };

  if (loading) return <TabSpinner label="Loading history..." />;
  if (error) return <div className="p-8 text-center text-sm text-[var(--semantic-error)]">Error loading history</div>;

  return (
    <>
    <div className="flex h-full flex-col bg-[var(--surface-panel-solid)]">
      <div className="px-4 pt-4">
        <TabHeader
          icon={FileText}
          title="Summary"
          subtitle="Cross-program history and documents"
        />
      </div>

      <div className="participant-card-toolbar sticky top-0 z-20 flex flex-nowrap items-center gap-2 px-3 py-2">
        <div className="flex min-w-0 flex-1 flex-nowrap items-center gap-2 overflow-x-auto">
          <div className="flex w-[190px] shrink-0 items-center gap-1.5">
            <Search className="h-3.5 w-3.5 text-[var(--neutral-gray-80)]" />
            <Input 
              id="participant-summary-search-input"
              placeholder="Search history..." 
              className="h-7 w-full max-w-none flex-1 bg-[var(--surface-panel-solid)] text-[12px] focus-visible:ring-1"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="ml-auto flex flex-nowrap items-center gap-1 pl-4">
            <Button
              id="participant-summary-toggle-expand-button"
              variant="outline"
              size="sm"
              onClick={() => setExpandAll(!expandAll)}
              className="h-7 shrink-0 px-1.5 text-[10px] font-medium uppercase"
              title={expandAll ? "Collapse All" : "Expand Text"}
            >
              <ChevronsUpDown className="h-3 w-3" />
              {expandAll ? "Collapse All" : "Expand Text"}
            </Button>

            {Object.entries(eventTypeLabels)
              .filter(([type]) => type !== 'progress_report' || canReadProgressReports)
              .map(([type, label]) => {
              const isSelected = selectedFilters.has(type as EventType);
              const style = getEventStyles(type as EventType);

              return (
                <Button
                  key={type}
                  id={`participant-summary-filter-${type}-button`}
                  variant="outline"
                  size="sm"
                  onClick={() => toggleFilter(type as EventType)}
                  className={cn(
                    "h-7 shrink-0 px-1.5 text-[10px] font-medium uppercase transition-colors",
                    isSelected
                      ? cn(style.bg, style.border, style.text, "shadow-none")
                      : "border-[var(--neutral-gray-40)] bg-[var(--surface-panel-solid)] text-[var(--neutral-gray-80)] hover:bg-[var(--surface-muted)]",
                  )}
                >
                  {isSelected && <Check className="h-3 w-3" />}
                  {label}
                </Button>
              );
            })}

            {onOpenMedcall ? (
              <Button
                id="participant-summary-open-medcall-button"
                variant="outline"
                size="sm"
                onClick={onOpenMedcall}
                className="h-7 shrink-0 px-1.5 text-[10px] font-medium uppercase"
              >
                <Pill className="h-3 w-3" />
                MedCall
              </Button>
            ) : null}
          </div>
        </div>

        {onAddEntry && (
          <Button
            id="summary-add-note-booking"
            size="sm"
            className="h-7 gap-1 px-2.5 text-[12px]"
            onClick={onAddEntry}
            title="Add a new note or booking for this participant"
          >
            <Plus className="h-3.5 w-3.5" />
            Add Event
          </Button>
        )}
      </div>

      {/* Timeline List */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        <div className="w-full max-w-[1200px] space-y-1">
          {filteredEvents.map((event) => (
            <TimelineItem 
              key={event.id} 
              event={event} 
              forceExpand={expandAll} 
              onViewDocument={handleViewDocument}
              onSelectScan={handleSelectScan}
              onOpenProgressReports={onOpenProgressReports}
            />
          ))}

          {filteredEvents.length === 0 && (
            <div className="py-8 text-center text-[12px] text-[var(--neutral-gray-80)]">
              No events found.
            </div>
          )}
        </div>
      </div>
    </div>

    <Dialog
      open={documentModalOpen}
      onOpenChange={(open: boolean) => {
        if (!open) {
          closeDocumentModal();
        }
      }}
    >
      <DialogContent
        className="flex h-full max-w-none flex-col gap-0 overflow-hidden p-0"
        contentWidth="92vw"
        contentMaxWidth="1120px"
        contentHeight="84vh"
        contentMaxHeight="84vh"
        onInteractOutside={(event) => event.preventDefault()}
      >
        <DialogHeader className="gap-1 border-b border-[var(--neutral-gray-30)] px-5 py-4">
          <DialogTitle>Document</DialogTitle>
          <DialogDescription className="flex flex-wrap items-center gap-2">
            {documentView?.type ? (
              <span className="text-sm text-[var(--neutral-gray-80)]">{documentView.type}</span>
            ) : (
              <span className="text-sm text-[var(--neutral-gray-80)]">Participant document preview</span>
            )}
            {documentView?.tags?.length ? (
              <span className="flex flex-wrap gap-1">
                {documentView.tags.map((tag) => (
                  <Badge
                    key={tag}
                    variant="outline"
                    className="h-5 border-[var(--azure-primary-border-soft)] bg-[var(--surface-panel-solid)] px-1.5 py-0 text-xs font-medium text-[var(--azure-primary)]"
                  >
                    {tag}
                  </Badge>
                ))}
              </span>
            ) : null}
            {documentLoading && (
              <span className="inline-flex items-center gap-1 text-xs text-[var(--neutral-gray-80)]">
                <RefreshCw className="h-3 w-3 animate-spin" />
                Loading...
              </span>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {renderDocumentModalContent()}
        </div>
      </DialogContent>
    </Dialog>
    {selectedScanId && (
      <ScanPreview
        odrnumber={odrnumber}
        scan={selectedScanId}
        close={() => setSelectedScanId(null)}
      />
    )}
    </>
  );
};

const truncateDescription = (text: string, maxLength: number) => {
  if (text.length <= maxLength) {
    return text;
  }
  return text.substring(0, maxLength) + '...';
};

const TimelineItem = ({ event, forceExpand, onViewDocument, onSelectScan, onOpenProgressReports }: { event: TimelineEvent, forceExpand: boolean, onViewDocument?: (documentId?: number) => void, onSelectScan?: (scanId: number) => void, onOpenProgressReports?: () => void }) => {
  const config = getEventConfig(event.type);
  const Icon = config.icon;
  const styles = getEventStyles(event.type);
  const isDocument = event.type === 'document';
  const documentTags = Array.isArray(event.metadata?.tags) ? event.metadata.tags : [];
  const documentId = event.metadata?.documentId as number | undefined;
  const isScanGroup = event.type === 'scan' && event.metadata?.scans;
  
  const [showFullDescription, setShowFullDescription] = useState(forceExpand);
  
  useEffect(() => {
    setShowFullDescription(forceExpand);
  }, [forceExpand]);

  const descriptionText = event.description || "";
  const displayDescription = showFullDescription 
    ? descriptionText 
    : truncateDescription(descriptionText, 84);

  return (
    <div className="flex items-stretch py-0.5">
      <div className="w-[62px] flex-shrink-0 pr-1.5 text-right">
        <div className="text-[12px] font-medium leading-none text-[var(--neutral-gray-90)]">
          {isoDateToMDY(event.date.toISOString())}
        </div>
      </div>

      <div className="relative flex w-4 flex-shrink-0 items-center justify-center">
        <div className="absolute bottom-0 top-0 w-px bg-[var(--neutral-gray-30)]" />
        <div className={cn("z-10 h-1.5 w-1.5 rounded-full border-2 border-white shadow-sm", styles.dot)} />
      </div>

      <div
        className={cn(
          "participant-card ml-1 flex flex-1 flex-col border-l-[3px] px-2.5 py-1.5 transition-colors hover:border-[var(--neutral-gray-40)]",
        )}
        style={{ borderLeftColor: styles.hex }}
      >
        <div className="flex items-start gap-1">
          <Icon className={cn("mt-0.5 h-3 w-3 flex-shrink-0", styles.text)} />

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1">
              <span className={cn("text-[12px] font-medium leading-4", styles.text)}>
                {event.title}
              </span>

              {event.type === 'coordinated_care' && event.metadata?.type && (
                <Badge variant="outline" className="h-4 border-[var(--neutral-gray-30)] bg-[var(--surface-panel-solid)] px-1 py-0 text-[12px] font-medium text-[var(--neutral-gray-100)]">
                  {event.metadata.type}
                </Badge>
              )}
              
              {event.type === 'note' && event.metadata?.code && (
                <span className="rounded border border-[var(--neutral-gray-30)] bg-[var(--surface-panel-solid)] px-1 text-[12px] font-medium text-[var(--neutral-gray-80)]">
                  {event.metadata.code}
                </span>
              )}
              {event.type === 'progress_report' && onOpenProgressReports && (
                <Button type="button" variant="ghost" size="sm" className="ml-auto h-5 px-1.5 text-[12px] text-[var(--azure-primary)]" onClick={onOpenProgressReports}>
                  View reports
                </Button>
              )}
            </div>

            {isScanGroup && (
              <div className="mt-0.5 flex flex-wrap gap-0.5">
                {event.metadata.scans.map((scan: any, idx: number) => (
                  <span
                    key={scan.id}
                    className="cursor-pointer break-words text-[10.5px] text-[var(--azure-primary)] hover:underline"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!onSelectScan) return;
                      onSelectScan(scan.id);
                    }}
                  >
                    {scan.label}{idx < event.metadata.scans.length - 1 ? "," : ""}
                  </span>
                ))}
              </div>
            )}

            {isDocument && (
                <div className="mb-0.5 mt-0.5 flex flex-wrap items-center gap-0.5">
                  {documentTags.length > 0 ? (
                    documentTags.map((tag: string) => (
                      <Badge 
                        key={tag} 
                        variant="outline" 
                        className="h-4 border-[var(--azure-primary-border-soft)] bg-[var(--surface-panel-solid)] px-1 py-0 text-[12px] font-medium text-[var(--azure-primary)]"
                      >
                        {tag}
                      </Badge>
                  ))
                ) : (
                  <span className="text-[9.5px] text-[var(--neutral-gray-80)]">No tags</span>
                )}
                {documentId && onViewDocument && (
                  <Button
                    id={`summary-document-view-${documentId}`}
                    variant="ghost"
                    size="sm"
                    className="ml-auto h-5 px-1.5 text-[12px] font-medium text-[var(--azure-primary)] hover:bg-[var(--azure-primary-soft)] hover:text-[var(--azure-primary-hover)]"
                    onClick={(e) => {
                      e.stopPropagation();
                      onViewDocument(documentId);
                    }}
                  >
                    <Eye className="mr-1 h-3 w-3" />
                    View document
                  </Button>
                )}
              </div>
            )}

            {!isScanGroup && descriptionText && (
              <div className="mt-0.5">
                <span 
                  className={cn(
                    "break-words text-[12px] leading-4 text-[var(--neutral-gray-100)]",
                    event.type === 'scan' ? "cursor-pointer text-[var(--azure-primary)] hover:underline" : ""
                  )} 
                  title={descriptionText}
                  onClick={(e) => {
                    if (event.type !== 'scan') return;
                    e.stopPropagation();
                    const scanId = event.metadata?.scanId || event.id.replace('scan-', '');
                    if (!onSelectScan) return;
                    onSelectScan(Number(scanId));
                  }}
                >
                  {displayDescription}
                </span>
                {descriptionText.length > 84 && (
                  <Button 
                    variant="link"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowFullDescription(!showFullDescription);
                    }} 
                    className="ml-1 h-auto p-0 text-[12px] font-medium"
                  >
                    {showFullDescription ? "Show Less" : "Show More"}
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
        
        {(event.isRange || event.metadata?.author) && (
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[12px] text-[var(--neutral-gray-80)]">
            {event.isRange && (
              <div className="flex items-center gap-0.5 rounded border border-[var(--neutral-gray-30)] bg-[var(--surface-panel-solid)] px-1 py-0">
                <span>Start: {isoDateToMDY(event.date.toISOString())}</span>
                <ArrowRight className="mx-0.5 h-2 w-2 text-[var(--neutral-gray-70)]" />
                <span>End: {event.endDate ? isoDateToMDY(event.endDate.toISOString()) : "Ongoing"}</span>
              </div>
            )}
            {event.metadata?.author && (
              <span className="ml-auto max-w-[110px] truncate italic">
                {event.metadata.author}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// --- HELPERS ---

const getEventConfig = (type: EventType) => {
  switch (type) {
    case 'enrollment': return { icon: Activity };
    case 'arrest': return { icon: Siren };
    case 'hearing': return { icon: Gavel };
    case 'note': return { icon: FileText }; // General Notes icon
    case 'coordinated_care': return { icon: ClipboardList };
    case 'scan': return { icon: ScanLine };
    case 'document': return { icon: FileText };
    case 'incident': return { icon: AlertTriangle };
    case 'key_event': return { icon: Star }; // Key Event icon
    case 'progress_report': return { icon: FileText };
    default: return { icon: FileText };
  }
};

const getEventStyles = (type: EventType) => {
  switch (type) {
    case 'enrollment': 
      return { 
        bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700",
        dot: "bg-emerald-500", hex: "var(--semantic-success)"
      };
    case 'arrest': 
      return { 
        bg: "bg-red-50", border: "border-red-200", text: "text-red-700",
        dot: "bg-red-500", hex: "var(--semantic-error)"
      };
    case 'hearing': 
      return { 
        bg: "bg-orange-50", border: "border-orange-200", text: "text-orange-700",
        dot: "bg-orange-500", hex: "#f97316"
      };
    case 'note': // General Notes style
      return { 
        bg: "bg-indigo-50", border: "border-indigo-200", text: "text-indigo-700",
        dot: "bg-indigo-500", hex: "#6366f1"
      };
    case 'coordinated_care': 
      return { 
        bg: "bg-purple-50", border: "border-purple-200", text: "text-purple-700",
        dot: "bg-purple-500", hex: "#a855f7"
      };
    case 'scan': 
      return { 
        bg: "bg-slate-50", border: "border-slate-200", text: "text-slate-700",
        dot: "bg-slate-500", hex: "#64748b"
      };
    case 'document':
      return {
        bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-700",
        dot: "bg-blue-500", hex: "#3b82f6"
      };
    case 'incident':
      return {
        bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700",
        dot: "bg-amber-500", hex: "#f59e0b"
      };
    case 'key_event':
      return {
        bg: "bg-pink-50", border: "border-pink-200", text: "text-pink-700",
        dot: "bg-pink-500", hex: "#ec4899"
      };
    case 'progress_report':
      return {
        bg: "bg-[var(--azure-primary-soft)]", border: "border-[var(--azure-primary-border-soft)]", text: "text-[var(--azure-primary)]",
        dot: "bg-[var(--azure-primary)]", hex: "var(--azure-primary)"
      };
    default: 
      return { 
        bg: "bg-gray-50", border: "border-gray-200", text: "text-gray-600",
        dot: "bg-gray-400", hex: "#9ca3af"
      };
  }
};
